import { useEffect, useState } from "react";
import clinicApi from "../../api/clinicClient";
import PortalLayout from "../layout/PortalLayout";

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

const TYPE_CFG = {
  consultation: { icon: "pi-stethoscope", bg: "#eff6ff", color: "#2563eb", label: "Consultation" },
  treatment:    { icon: "pi-heart",       bg: "#ecfdf5", color: "#059669", label: "Treatment" },
  prescription: { icon: "pi-file-edit",   bg: "#fef3c7", color: "#b45309", label: "Prescription" },
};

function RecordDetail({ record }) {
  const d = record.data || {};
  if (record.record_type === "consultation") {
    return (
      <div style={{ fontSize: 12.5, color: "var(--text-secondary)" }}>
        {d.diagnosis_notes && <div style={{ marginBottom: 4 }}>{d.diagnosis_notes}</div>}
        {d.vitals && Object.keys(d.vitals).length > 0 && (
          <div style={{ color: "var(--text-muted)" }}>
            {Object.entries(d.vitals).map(([k, v]) => `${k}: ${v}`).join(" · ")}
          </div>
        )}
        {d.follow_up_date && <div style={{ color: "var(--text-muted)", marginTop: 4 }}>Follow-up: {formatDate(d.follow_up_date)}</div>}
      </div>
    );
  }
  if (record.record_type === "treatment") {
    return (
      <div style={{ fontSize: 12.5, color: "var(--text-secondary)" }}>
        {d.notes && <div>{d.notes}</div>}
      </div>
    );
  }
  if (record.record_type === "prescription") {
    return (
      <div style={{ fontSize: 12.5, color: "var(--text-secondary)" }}>
        {(d.items || []).map((item, idx) => (
          <div key={idx}>
            {item.product_name}{item.dosage ? ` — ${item.dosage}` : ""}{item.frequency ? `, ${item.frequency}` : ""}
          </div>
        ))}
      </div>
    );
  }
  return null;
}

function TimelineItem({ record }) {
  const cfg = TYPE_CFG[record.record_type] ?? { icon: "pi-circle", bg: "#f3f4f6", color: "#6b7280", label: record.record_type };
  return (
    <div className="ph-card" style={{ padding: "14px 16px", display: "flex", gap: 12 }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10, flexShrink: 0,
        background: cfg.bg, color: cfg.color,
        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15,
      }}>
        <i className={`pi ${cfg.icon}`} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 13.5, fontWeight: 700, color: "var(--text-primary)" }}>{record.title}</span>
          <span style={{ fontSize: 11.5, color: "var(--text-muted)", flexShrink: 0 }}>{formatDate(record.event_date)}</span>
        </div>
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 6 }}>
          {cfg.label} · {record.source_clinic_name}{record.doctor_name ? ` · Dr. ${record.doctor_name}` : ""}
        </div>
        <RecordDetail record={record} />
      </div>
    </div>
  );
}

export default function HealthHistory() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");

  useEffect(() => {
    clinicApi.get("health-history/")
      .then(res => setRecords(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = typeFilter === "all" ? records : records.filter(r => r.record_type === typeFilter);

  return (
    <PortalLayout>
      <div className="page-header">
        <h1 className="page-title">Health History</h1>
        <p className="page-subtitle">Your consolidated clinical record — stays with you, not any one clinic</p>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {[
          { key: "all", label: "All" },
          { key: "consultation", label: "Consultations" },
          { key: "treatment", label: "Treatments" },
          { key: "prescription", label: "Prescriptions" },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setTypeFilter(key)}
            style={{
              padding: "6px 14px", borderRadius: 20, fontSize: 12.5, fontWeight: 500,
              border: `1.5px solid ${typeFilter === key ? "var(--brand-primary)" : "var(--border-color)"}`,
              background: typeFilter === key ? "var(--brand-primary)" : "transparent",
              color: typeFilter === key ? "white" : "var(--text-secondary)",
              cursor: "pointer", fontFamily: "inherit",
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "48px 20px", color: "var(--text-muted)" }}>
          <i className="pi pi-spin pi-spinner" style={{ fontSize: 28, display: "block", marginBottom: 12, color: "var(--brand-primary)" }} />
          <div style={{ fontSize: 13 }}>Loading health history…</div>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 20px", color: "var(--text-muted)" }}>
          <i className="pi pi-heart" style={{ fontSize: 32, display: "block", marginBottom: 12 }} />
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>No health records yet</div>
          <div style={{ fontSize: 13 }}>Records from your clinic visits will appear here permanently.</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {filtered.map(r => <TimelineItem key={r.id} record={r} />)}
        </div>
      )}
    </PortalLayout>
  );
}
